import { Button } from './ui/Button';
import { useDsTranslate } from '../context/DsProvider';
import { CheckIcon, PencilIcon } from './reviewIcons';

interface ReviewerActionsProps {
  /** Current reviewer mode. Decides which pair of actions is offered. */
  mode: 'preview' | 'edit';
  submitPending: boolean;
  completePending: boolean;
  anyPending: boolean;
  /**
   * The document on the right has finished loading.
   *
   * Both actions are decisions ABOUT the document, and until it is on screen the reviewer is
   * being asked to make them blind — on a slow DOCX the editor takes several seconds while
   * Approve sits there live. Disabled rather than hidden: unlike a finished review, the wait
   * ends on its own, so the control should stay where the eye already is.
   */
  documentReady: boolean;
  onSubmit: () => void;
  onComplete: () => void;
  onApprove: () => void;
  onRequestChanges: () => void;
  /** Native-PDF snapshot — no DOCX to edit, so there is nothing to request changes on. */
  hideRequestChanges?: boolean;
  /**
   * Hide "Mark as reviewed" (edit mode only).
   *
   * For the one reviewer who is not reviewing someone else's contract: the client who pressed
   * "Request changes" on their own EOR draft is made the review's only reviewer, and marking it
   * reviewed would close their own request without Workpay ever reading what they marked up.
   * Their way out is Request changes, or Cancel review.
   */
  hideComplete?: boolean;
}

/**
 * The two decisions on the reviewer screen — the SAME two, on both screens.
 *
 * Moved into ds-shared because "the client portal and the external reviewer should look and
 * behave the same" (demo feedback) was true of everything around these buttons and not of the
 * buttons themselves: each app passed its own, so the external reviewer got 12px type in a
 * neutral-grey button where the portal admin got 14px in a brand-outlined one, in the same
 * slot of the same shared chrome. One component, one set of labels, one geometry.
 *
 *   preview: [Request changes]  [Approve]
 *   edit:    [Request changes]  [Mark as reviewed]
 *
 * Header, context strip and rails come from `ReviewerChrome`, which the external reviewer
 * screen wears too — this component is the half that differs, because only this surface has a
 * PDF preview to approve from.
 *
 * Buttons stay MOUNTED during any in-flight mutation, each showing its own spinner via
 * `loading`; both are disabled while either is pending so a second click cannot race the first.
 */
export function ReviewerActions({
  mode,
  submitPending,
  completePending,
  anyPending,
  documentReady,
  onSubmit,
  onComplete,
  onApprove,
  onRequestChanges,
  hideRequestChanges = false,
  hideComplete = false,
}: ReviewerActionsProps) {
  const t = useDsTranslate();
  const blocked = anyPending || !documentReady;

  if (mode === 'preview') {
    return (
      <>
        {!hideRequestChanges && (
          <Button
            variant="outline"
            size="sm"
            icon={<PencilIcon />}
            disabled={blocked}
            onClick={onRequestChanges}
          >
            {t('K_DS_REVIEWER_REQUEST_CHANGES', 'Request changes')}
          </Button>
        )}
        <Button
          variant="primary"
          size="sm"
          icon={<CheckIcon />}
          loading={completePending}
          disabled={blocked}
          onClick={onApprove}
        >
          {t('K_DS_REVIEWER_APPROVE', 'Approve')}
        </Button>
      </>
    );
  }

  return (
    <>
      <Button
        /* The only action when there is no Mark as reviewed beside it, so it takes the weight. */
        variant={hideComplete ? 'primary' : 'outline'}
        size="sm"
        icon={<PencilIcon />}
        loading={submitPending}
        disabled={blocked}
        onClick={onSubmit}
      >
        {/* WP-16180 — ONE verb for one action, on the button, the dialog it opens, the
            dialog's confirm and the "How to review" rail. The button said "Submit changes"
            while the dialog it raised was titled "Request changes?" and confirmed with
            "Request changes": the reviewer pressed one name and was asked to agree to
            another. The same key as the preview-mode button above, because it is the same
            action — `K_REVIEWER_SUBMIT_CHANGES` was the second name and is now unused. */}
        {t('K_DS_REVIEWER_REQUEST_CHANGES', 'Request changes')}
      </Button>
      {/* "Mark as reviewed", matching the external screen and the prototype. It read "Mark as
          Complete" here and "Mark as reviewed" there — the same action, named two ways, on the
          two screens this feature most needs to look like one another. */}
      {!hideComplete && (
        <Button
          variant="primary"
          size="sm"
          icon={<CheckIcon />}
          loading={completePending}
          disabled={blocked}
          onClick={onComplete}
        >
          {t('K_DS_EXT_REVIEW_BUTTON_MARK_REVIEWED', 'Mark as reviewed')}
        </Button>
      )}
    </>
  );
}

export default ReviewerActions;
